import { CircleHelpIcon, SparklesIcon, UnplugIcon, XIcon } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { DialogClose } from '@/components/ui/dialog.js';
import { HintCard } from '@/features/onboarding/HintCard.js';
import { HINTS } from '@/features/onboarding/hint-config.js';
import { isNodeSeen } from '@/features/onboarding/hint-store.js';
import { notifyHintTargetsChanged } from '@/features/onboarding/hint-targets.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { useWorkspaceSharing } from '@/features/workspace/useWorkspaceSharing.js';
import { SkillsPane } from '@/features/skills/SkillsPane.js';
import type { Skill } from '@/lib/api/skills.js';
import { skillsGuideUrl, type SkillsGuideSection } from '@/lib/guide-links.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import { cn } from '@/lib/utils.js';

import { ConnectorsDirectory } from './ConnectorsDirectory.js';
import { ConnectorScopeProvider } from './connector-scope.js';

/** Rail category shown in the directory modal's content pane. */
type DirectoryCategory = 'connectors' | 'skills';

interface ConnectorsDirectoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The workspace whose connectors the modal manages. Omitted from chat, where it follows the
   * selected workspace (the sidebar switcher). Passed explicitly by any surface that targets a
   * specific workspace.
   */
  workspaceId?: string | null;
  /**
   * Which rail category shows first. The caller only mounts this component
   * while `open` is true (see `ComposerPlusMenu`), so each open is a fresh
   * mount and this only matters at open time — it isn't a live prop.
   */
  initialCategory?: DirectoryCategory;
  /**
   * Which scope tab the Skills pane opens on. The workspace manage page opens the modal straight on
   * its own workspace's skills; chat leaves it unset and lands on Personal.
   */
  initialSkillScope?: 'private' | 'workspace' | 'corporate';
  /**
   * Which skill's card the Skills pane opens on. Read at open time like the two fields above; chat's
   * accept-a-suggestion path sets it so the user lands on the skill they just created.
   */
  initialSkillId?: string | null;
  /**
   * Forwarded to `SkillsPane`'s detail view: called when the user clicks
   * Use on a skill. The modal always closes itself first; Task 18 wires the
   * composer's actual behavior here.
   */
  onUseSkill?: (skill: Skill) => void;
  /** Forwarded to the dialog: lets the host send focus somewhere other than the opening control. */
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * Directory modal — Anthropic-style shell with a left category rail and a
 * content pane, currently routing between "Connectors" and the feature-gated
 * "Skills" category.
 *
 * Connectors are a workspace resource (global-workspaces phase 2.5): the body is scoped to a
 * workspace via `ConnectorScopeProvider`. The same body renders in the manage tab. Skills are
 * scoped the same way — the manage page's Agent tab opens this modal on its own workspace's skills,
 * which is why `SkillsPane` takes the resolved id rather than reading the switcher itself.
 *
 * UX conventions:
 *   • No element receives focus on open (no stray focus ring on close btn).
 *   • Neutral palette — no accent / brand color on action affordances.
 *   • Workspace-sized: ~48rem tall on large viewports, full-height on mobile.
 */
export function ConnectorsDirectoryModal({
  open,
  onOpenChange,
  workspaceId,
  initialCategory,
  initialSkillScope,
  initialSkillId,
  onUseSkill,
  onCloseAutoFocus,
}: ConnectorsDirectoryModalProps): JSX.Element {
  const selectedWorkspaceId = useUiStore((state) => state.selectedWorkspaceId);
  const scopeWorkspaceId =
    workspaceId === undefined ? selectedWorkspaceId : workspaceId;
  /**
   * Whether the scoped workspace is the caller's personal one. The connector cards need it to decide
   * whether a SharePoint connect asks whose directory it is, and the modal is the surface that holds
   * only an id — the manage tab passes the row it already read.
   */
  const sharing = useWorkspaceSharing(scopeWorkspaceId);
  const [activeCategory, setActiveCategory] = useState<DirectoryCategory>(
    initialCategory ?? 'connectors',
  );
  /**
   * Defensive clamp: `CategoryRail` already omits the Skills `<li>` when the
   * flag is off, but `activeCategory` state can still be `'skills'` — either
   * from a stale `initialCategory` prop, or the flag flipping off mid-session
   * (an admin kill-switch, or `connector.changed` triggering a config
   * reload). Rendering must key off this, not the raw state, so a killed
   * feature never shows its pane even if something set the state to it.
   */
  const skillsEnabled = getRuntimeConfig().features.skills;
  const effectiveCategory: DirectoryCategory = skillsEnabled
    ? activeCategory
    : 'connectors';
  /** Skills-pane search text — connectors search lives inside `ConnectorsDirectory`. */
  const [query, setQuery] = useState('');
  /**
   * Which guide section the header's question mark leads to. The Skills pane reports it,
   * because the answer depends on the tab it is on and whether a skill is open; connectors
   * have no guide page of their own yet, so the link is only offered for skills.
   */
  const [guideSection, setGuideSection] =
    useState<SkillsGuideSection>('library');
  const handleGuideSectionChange = useCallback(
    (section: SkillsGuideSection) => setGuideSection(section),
    [],
  );

  /** Switching category resets the search box — trigger/title text on a skill
   *  isn't a meaningful filter for connectors and vice versa. The guide section resets with
   *  it: the pane unmounts on the way out and reports again on the way back, so keeping the
   *  old value would leave the question mark pointing at the section of a view that is no
   *  longer open until that report lands. */
  function handleCategoryChange(category: DirectoryCategory): void {
    setActiveCategory(category);
    setQuery('');
    setGuideSection('library');
  }

  /** The modal always closes on Use — Task 18 supplies the real composer callback. */
  function handleUseSkill(skill: Skill): void {
    onOpenChange(false);
    onUseSkill?.(skill);
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      onCloseAutoFocus={onCloseAutoFocus}
      title="Directory"
      description="Browse and connect Customertimes and partner integrations."
      titleHidden
      showCloseButton={false}
      aria-labelledby="directory-title"
      className="flex w-full max-w-[var(--dialog-w-lg)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl h-[min(48rem,calc(100dvh-3rem))] max-md:max-w-[calc(100%-1.5rem)] max-md:h-[calc(var(--app-height,100dvh)-1.5rem)]"
    >
      {/* Top bar — spans full width above rail + content */}
      <div className="flex items-center justify-between border-b border-border/40 px-4 py-4 md:px-6">
        <div>
          <h2
            id="directory-title"
            className="text-base font-semibold tracking-tight md:text-[1.0625rem]"
          >
            Directory
          </h2>
          <p className="mt-0.5 text-caption leading-snug text-muted-foreground">
            {effectiveCategory === 'skills'
              ? 'Reusable instructions the agent can run on demand'
              : 'Customertimes & partner integrations'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {effectiveCategory === 'skills' && (
            <a
              href={skillsGuideUrl(guideSection)}
              target="_blank"
              rel="noopener noreferrer"
              title="Open the skills guide"
              aria-label="Open the skills guide"
              className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
            >
              <CircleHelpIcon className="size-4" aria-hidden="true" />
            </a>
          )}
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
            >
              <XIcon className="size-4" aria-hidden="true" />
            </button>
          </DialogClose>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-row">
        <CategoryRail
          activeCategory={effectiveCategory}
          onCategoryChange={handleCategoryChange}
        />
        {effectiveCategory === 'skills' ? (
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            <SkillsPane
              query={query}
              onQueryChange={setQuery}
              onUseSkill={handleUseSkill}
              workspaceId={scopeWorkspaceId}
              initialScope={initialSkillScope}
              initialSkillId={initialSkillId}
              onGuideSectionChange={handleGuideSectionChange}
              onLeaveForChat={() => onOpenChange(false)}
            />
            <DirectoryHintOverlay id="composer.plus.skills" />
          </div>
        ) : (
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            <ConnectorScopeProvider
              value={{
                workspaceId: scopeWorkspaceId,
                canManage: true,
                sharing,
              }}
            >
              <ConnectorsDirectory />
            </ConnectorScopeProvider>
            <DirectoryHintOverlay id="composer.plus.connectors" />
          </div>
        )}
      </div>
    </AppDialog>
  );
}

/**
 * Full-pane onboarding tip over the directory content. Panes keep their own
 * scroll; the tip overlays instead of stacking so a tall video never squeezes
 * the list. Visibility is latched in React state (not CSS :has / :empty).
 */
function DirectoryHintOverlay({ id }: { id: string }): JSX.Element | null {
  const [show, setShow] = useState(() => Boolean(HINTS[id]) && !isNodeSeen(id));

  useEffect(() => {
    notifyHintTargetsChanged();
    return () => notifyHintTargetsChanged();
  }, [show]);

  if (!show) return null;

  return (
    <div className="absolute inset-0 z-10 overflow-y-auto overscroll-contain bg-background px-4 pt-4 md:px-6">
      <HintCard
        id={id}
        onDismissed={() => {
          setShow(false);
        }}
      />
    </div>
  );
}

/**
 * Category rail. Selection is lifted to the parent, which routes the content
 * pane on it. The Skills item is feature-gated the same way the composer's
 * "Skills" menu entry is — a killed feature must not leak into the modal
 * even when it's opened from the always-visible Connectors entry point.
 *
 * Hidden on mobile (`max-md:hidden`, unchanged from v1) — there is no
 * category switcher on small viewports yet; see Task 16 follow-ups.
 */
function CategoryRail({
  activeCategory,
  onCategoryChange,
}: {
  activeCategory: DirectoryCategory;
  onCategoryChange: (category: DirectoryCategory) => void;
}): JSX.Element {
  return (
    <nav
      aria-label="Directory categories"
      className="shrink-0 border-r border-border/40 p-3 md:w-52 md:p-4 max-md:hidden"
    >
      <ul className="flex flex-col gap-0.5">
        <li>
          <CategoryRailButton
            active={activeCategory === 'connectors'}
            label="Connectors"
            onClick={() => onCategoryChange('connectors')}
          >
            <UnplugIcon
              className="size-4 shrink-0"
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </CategoryRailButton>
        </li>
        {getRuntimeConfig().features.skills && (
          <li>
            <CategoryRailButton
              active={activeCategory === 'skills'}
              label="Skills"
              onClick={() => onCategoryChange('skills')}
            >
              <SparklesIcon
                className="size-4 shrink-0"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </CategoryRailButton>
          </li>
        )}
      </ul>
    </nav>
  );
}

function CategoryRailButton({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20',
        active
          ? 'bg-muted text-foreground'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
      )}
    >
      {children}
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}
